import React, { createContext, ReactNode, useCallback, useContext, useEffect } from 'react';

import { ConsolidatedForecastListItem, Consolidation } from '@futrli-sdk/consolidations';
import { Logger, usePromiseState } from '@futrli/shared-services';

import { readConsolidatedForecasts } from '../../../core/api/consolidated-forecasting';
import { useConsolidationDataContext } from '../../../predict-management/context/consolidation-data-context';

interface ConsolidatedForecastingContextProps {
  children?: ReactNode;
}

interface ConsolidatedForecastingContextValues {
  loadForecasts: () => Promise<void>;
  forecasts?: ConsolidatedForecastListItem[];
  isLoading: boolean;
  consolidation?: Consolidation;
}

const context = createContext(({} as unknown) as ConsolidatedForecastingContextValues);

export const ConsolidatedForecastingContext = ({ children }: ConsolidatedForecastingContextProps) => {
  const { workspaceId, consolidationId, consolidation } = useConsolidationDataContext();
  const [forecastsReq, setForecastsReq] = usePromiseState<ConsolidatedForecastListItem[]>();

  const loadForecasts = useCallback(async () => {
    try {
      await setForecastsReq(readConsolidatedForecasts(workspaceId, consolidationId));
    } catch (e) {
      Logger.error(e as string);
    }
  }, [consolidationId, setForecastsReq, workspaceId]);

  useEffect(() => {
    loadForecasts();
  }, [loadForecasts]);

  const values = {
    forecasts: forecastsReq.data,
    isLoading: forecastsReq.busy,
    loadForecasts,
    consolidation
  };

  return <context.Provider value={values}>{children}</context.Provider>;
};

export const useConsolidatedForecastingContext = () => useContext(context);
